import { Heading } from "@chakra-ui/react";
import { serverSideTranslations } from "next-i18next/pages/serverSideTranslations";
import type { ReactElement } from "react";

import { T_KEY } from "@/src/constants/translationsKey";

import HeadMeta from "../../src/components/common/HeadMeta/HeadMeta";
import CenteredLayout from "../../src/components/common/Layout/CenteredLayout";
import GuideList from "../../src/components/customer-service/Guide/List/GuideList";
import { Item } from "../../src/components/customer-service/ItemList/ItemList";
import Transl from "../../src/components/translation/Transl";
import { CS_OG_MESSAGES } from "../../src/constants/constants";
import { GUIDE_CONTENTS } from "../../src/constants/constants_guideContents";
import useAppStateStore from "../../src/store/client/appState";
import { Locale, LOCALES_SHORT } from "../../src/utils/types";

import styles from "./cs.module.scss";

export const mapTitlesToGuideListItem = (
  locale = LOCALES_SHORT.EN,
  titles: Omit<Item, "link">[] = []
) =>
  titles?.map(item => ({
    category: item.category,
    id: item.id,
    title: item.title,
    link: `/${locale}/cs/faq/${item.id}`,
  }));

interface CustomerServiceProps {
  titles?: Item[];
  locale: Locale;
}

export default function CustomerService({
  titles = [],
  locale,
}: CustomerServiceProps): ReactElement | null {
  const isGolffixApp = useAppStateStore(state => state.isGolffixApp);
  const GuideListListItem = mapTitlesToGuideListItem(locale, titles);
  // eslint-disable-next-line security/detect-object-injection -- locale은 Next i18n 설정에 정의된 Locale union 값
  const ogMessage = CS_OG_MESSAGES[locale] || CS_OG_MESSAGES[LOCALES_SHORT.EN];
  // isGolffixApp은 클라이언트 UA 감지 값이라 SSR에서는 항상 null — null을 웹 기본값으로
  // 렌더해야 크롤러가 메타·본문이 포함된 HTML을 받는다 (SD-8408). 앱 웹뷰는 하이드레이션
  // 직후 감지되어 로고·푸터가 숨겨진다.
  const isWebView = isGolffixApp === true;
  // 비접두 /cs는 i18n 라우팅용 __default locale로 서빙된다 — 콘텐츠가 en 폴백과 동일하므로
  // canonical/og URL은 en으로 정규화한다 (/__default/cs 같은 무효 URL 방지)
  const urlLocale =
    !locale || String(locale) === "__default" ? LOCALES_SHORT.EN : locale;

  return (
    <CenteredLayout
      locale={locale}
      showLogo={!isWebView}
      showFooter={!isWebView}
      isWebView={isWebView}
      showUserMenuOrLoginButton
      centered
      // ! : 고객센터 페이지에서는 사용자 메뉴와 로그인 버튼을 보여준다.
    >
      <div className={styles.csWrapper}>
        <HeadMeta
          {...ogMessage}
          image="/images/customer-service/og.png"
          url={`https://web.golffix.io/${urlLocale}/cs`}
          locale={locale}
          isWebView={isWebView}
        />
        <div className={styles.contentContainer}>
          <Heading
            as="h1"
            size="xl"
            fontWeight="700"
            color="gray.800"
            mt="60px"
            mb="64px"
          >
            {/* // FIXME: 나중에 줄바꿈 들어갈 수 있음, 앱 공용 문구라 안 넣은 상태 */}
            <Transl useTranslationKey={T_KEY} i18nKey="support_greeting" />
          </Heading>

          {GuideListListItem?.length > 0 && (
            <GuideList items={GuideListListItem} />
          )}
        </div>
      </div>
    </CenteredLayout>
  );
}

const getTitles = (locale: Locale, mainContentsIds: number[]) => {
  const guideContents =
    GUIDE_CONTENTS[`${locale}`] || GUIDE_CONTENTS[LOCALES_SHORT.EN];

  return mainContentsIds
    .map(id => {
      const { title, category } = guideContents[`${id}`] || {};

      return {
        category: category || "",
        title: title || "",
        id: id.toString(),
      };
    })
    .filter(item => item.title && item.category);
};

export const getStaticProps = async ({
  locale,
  defaultLocale,
}: {
  locale: Locale;
  defaultLocale: Locale;
}) => {
  // 기획 파트에서 정의한 메인 컨텐츠 아이디 목록에 따라 추후 변경될 수 있으나 현재는 고정된 1,2,3,4,5에 대한 데이터만 제공
  const mainContentsIds = [
    1, 2, 3, 4, 5, 6,
    // 회원 탈퇴 가이드
    7,
  ];
  const titles = getTitles(locale, mainContentsIds);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? defaultLocale, [T_KEY])),
      locale: locale ?? defaultLocale, // locale 값이 undefined일 경우 null로 반환
      titles,
    },
    revalidate: 5,
  };
};
